import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare,
  ArrowLeft,
  Mail,
  MailOpen,
  Calendar,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WorkerMessages = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      // Fetch both messages and notifications
      const [messagesRes, notificationsRes] = await Promise.all([
        axiosInstance.get('/messages'),
        axiosInstance.get('/notifications')
      ]);
      
      // Combine messages and notifications into one list
      const messagesData = messagesRes.data.map(m => ({
        ...m,
        type: 'message',
        createdAt: m.sentAt || m.createdAt
      }));
      
      const notificationsData = notificationsRes.data.notifications.map(n => ({
        ...n,
        type: 'notification',
        subject: n.title,
        message: n.message,
        read: n.read
      }));
      
      // Merge and sort by date (newest first)
      const combined = [...messagesData, ...notificationsData].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setMessages(combined);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error(t('messages.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setShowMessageDialog(true);

    // Mark as read if unread
    if (!message.read) {
      try {
        if (message.type === 'notification') {
          await axiosInstance.put(`/notifications/${message.id}/read`);
        } else {
          await axiosInstance.post(`/messages/${message.id}/mark-read`);
        }
        // Update local state
        setMessages(messages.map(m => 
          m.id === message.id ? { ...m, read: true } : m
        ));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const deleteMessage = async (messageId, messageType) => {
    try {
      if (messageType === 'notification') {
        await axiosInstance.delete(`/notifications/${messageId}`);
      } else {
        // Delete regular message
        await axiosInstance.delete(`/messages/${messageId}`);
      }
      
      // Remove from local state
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success(t('messages.deleted'));
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(t('messages.failedDelete'));
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('messages.loadingMessages')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('messages.title')}</h1>
              <p className="text-gray-600 mt-1">{t('messages.viewNotificationsDesc')}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('messages.totalMessages')}</p>
                  <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unread</p>
                  <p className="text-2xl font-bold text-blue-600">{unreadCount}</p>
                </div>
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Read</p>
                  <p className="text-2xl font-bold text-gray-600">{messages.length - unreadCount}</p>
                </div>
                <MailOpen className="w-8 h-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Messages List */}
        <div className="space-y-4">
          {messages.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No messages yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  You'll receive notifications and announcements here
                </p>
              </CardContent>
            </Card>
          ) : (
            messages.map((message) => (
              <Card
                key={message.id}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  !message.read ? 'bg-blue-50 border-blue-200' : ''
                }`}
                onClick={() => openMessage(message)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-full ${
                        !message.read ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {!message.read ? (
                          <Mail className="w-5 h-5 text-blue-600" />
                        ) : (
                          <MailOpen className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-lg">
                            {message.title || 'Notification'}
                          </CardTitle>
                          {!message.read && (
                            <Badge variant="default" className="bg-blue-500">
                              New
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {message.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>
                            {new Date(message.createdAt).toLocaleDateString()} at{' '}
                            {new Date(message.createdAt).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMessage(message.id, message.type);
                      }}
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Message Detail Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedMessage?.title || 'Message'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>
                {selectedMessage?.createdAt && 
                  new Date(selectedMessage.createdAt).toLocaleDateString()} at{' '}
                {selectedMessage?.createdAt &&
                  new Date(selectedMessage.createdAt).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
              </span>
            </div>
            
            {selectedMessage?.priority === 'urgent' && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-semibold text-red-900">{t('messages.urgentMessage')}</span>
              </div>
            )}

            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">
                {selectedMessage?.message}
              </p>
            </div>

            {selectedMessage?.actionUrl && (
              <div className="pt-4">
                <Button
                  onClick={() => window.open(selectedMessage.actionUrl, '_blank')}
                  className="w-full"
                >
                  View Details
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerMessages;
